import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Row, Col, Card, CardBody, Badge, Button,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import { ArrowLeft, Pause, Play, XCircle, Copy, RefreshCw } from 'react-feather';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro } from '../../utils/alerts';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';

const STATUS_LABEL = {
  draft: 'Rascunho', scheduled: 'Agendada', running: 'Em disparo',
  paused: 'Pausada', completed: 'Concluída', cancelled: 'Cancelada',
};
const STATUS_COLOR = {
  draft: 'secondary', scheduled: 'primary', running: 'success',
  paused: 'warning', completed: 'info', cancelled: 'danger',
};

export default function CampanhaDetalhe() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useProspectAuth();
  const isAdmin   = user?.role === 'admin';

  const [campaign, setCampaign]   = useState(null);
  const [schedule, setSchedule]   = useState([]);
  const [flowSteps, setFlowSteps] = useState([]);
  const [tab, setTab]             = useState('0');
  const [loading, setLoading]     = useState(true);
  const autoRefresh               = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [campR, schR, flowR] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/schedule`),
        api.get(`/campaigns/${id}/flow`),
      ]);
      setCampaign(campR.data);
      setSchedule(schR.data);
      setFlowSteps(flowR.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    autoRefresh.current = setInterval(fetchData, 30000);
    return () => clearInterval(autoRefresh.current);
  }, [fetchData]);

  async function changeStatus(status) {
    try {
      await api.put(`/campaigns/${id}/status`, { status });
      fetchData();
    } catch (e) { alertErro('Erro', e.response?.data?.error || 'Não foi possível alterar o status'); }
  }

  async function duplicate() {
    try {
      const { data } = await api.post(`/campaigns/${id}/duplicate`);
      navigate(`/campanhas/${data.id}`);
    } catch (e) { alertErro('Erro ao duplicar', e.response?.data?.error || 'Não foi possível duplicar a campanha'); }
  }

  if (loading) return (
    <>
      <Breadcrumbs mainTitle='Detalhe da Campanha' parent='Topo Prospect' title='Campanha' />
      <Container fluid>
        <div className='d-flex align-items-center justify-content-center' style={{ height: 200 }}>
          <RefreshCw size={20} className='me-2' style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
        </div>
      </Container>
    </>
  );

  if (!campaign) return (
    <>
      <Breadcrumbs mainTitle='Detalhe da Campanha' parent='Topo Prospect' title='Campanha' />
      <Container fluid>
        <p className='text-muted'>Campanha não encontrada.</p>
      </Container>
    </>
  );

  const c = campaign;
  const m = c.metrics || {};
  const replyRate = m.sent > 0 ? Math.round((m.replied / m.sent) * 100) : 0;
  const donutData = [
    { name: 'Enviadas',  value: m.sent    || 0, color: '#0ea5e9' },
    { name: 'A enviar',  value: m.pending || 0, color: '#e2e8f0' },
  ];
  const pieData = [
    { name: 'Respondidas',     value: m.replied || 0,                                       color: '#22c55e' },
    { name: 'Não respondidas', value: Math.max(0, (m.sent || 0) - (m.replied || 0)),        color: '#f1f5f9' },
  ];

  const editable = ['draft', 'paused'].includes(c.status);
  const DAYS_LABEL = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <>
      <Breadcrumbs mainTitle={c.name} parent='Topo Prospect' title='Campanha' />
      <Container fluid>

        {/* Header */}
        <div className='d-flex align-items-start justify-content-between mb-4'>
          <div className='d-flex align-items-center gap-2'>
            <Button color='light' size='sm' onClick={() => navigate('/campanhas')}>
              <ArrowLeft size={16} />
            </Button>
            <div>
              <div className='d-flex align-items-center gap-2'>
                <h4 className='mb-0 fw-bold'>{c.name}</h4>
                <Badge color={STATUS_COLOR[c.status]}>{STATUS_LABEL[c.status]}</Badge>
              </div>
              <small className='text-muted'>
                {(m.total_contacts || 0).toLocaleString('pt-BR')} contatos · Agente: {c.agent_id}
              </small>
            </div>
          </div>
          <div className='d-flex gap-2'>
            {!isAdmin && c.status === 'running' && (
              <Button color='warning' size='sm' outline onClick={() => changeStatus('paused')}>
                <Pause size={14} className='me-1' /> Pausar
              </Button>
            )}
            {!isAdmin && c.status === 'paused' && (
              <Button color='success' size='sm' outline onClick={() => changeStatus('running')}>
                <Play size={14} className='me-1' /> Retomar
              </Button>
            )}
            {!isAdmin && ['running', 'paused', 'scheduled'].includes(c.status) && (
              <Button color='danger' size='sm' outline onClick={() => changeStatus('cancelled')}>
                <XCircle size={14} className='me-1' /> Cancelar
              </Button>
            )}
            {!isAdmin && (
              <Button color='light' size='sm' onClick={duplicate}>
                <Copy size={14} className='me-1' /> Duplicar
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Nav tabs className='mb-4'>
          <NavItem>
            <NavLink className={tab === '0' ? 'active' : ''} onClick={() => setTab('0')} style={{ cursor: 'pointer' }}>
              Dashboard
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink className={tab === '1' ? 'active' : ''} onClick={() => setTab('1')} style={{ cursor: 'pointer' }}>
              Configurações
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={tab}>

          {/* ── Dashboard ── */}
          <TabPane tabId='0'>
            <Row className='mb-4'>
              <Col md={3}>
                <MetricCard label='Total disparos'     value={m.sent        || 0} />
              </Col>
              <Col md={3}>
                <MetricCard label='Taxa de resposta'   value={`${replyRate}%`} />
              </Col>
              <Col md={3}>
                <MetricCard label='Leads qualificados' value={m.qualified   || 0} />
              </Col>
              <Col md={3}>
                <MetricCard label='Conexões usadas'    value={(c.connection_ids || []).length} />
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col md={6}>
                <Card>
                  <CardBody>
                    <p className='fw-semibold mb-3'>Progresso de envios</p>
                    <ResponsiveContainer width='100%' height={200}>
                      <PieChart>
                        <Pie data={donutData} cx='50%' cy='50%' innerRadius={55} outerRadius={80} paddingAngle={2} dataKey='value'>
                          {donutData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => v.toLocaleString('pt-BR')} />
                        <Legend iconType='circle' iconSize={10} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <CardBody>
                    <p className='fw-semibold mb-3'>Respostas</p>
                    <ResponsiveContainer width='100%' height={200}>
                      <PieChart>
                        <Pie data={pieData} cx='50%' cy='50%' outerRadius={80} paddingAngle={2} dataKey='value'>
                          {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => v.toLocaleString('pt-BR')} />
                        <Legend iconType='circle' iconSize={10} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <p className='text-muted text-end' style={{ fontSize: '0.75rem' }}>
              Atualiza automaticamente a cada 30s
            </p>
          </TabPane>

          {/* ── Configurações ── */}
          <TabPane tabId='1'>
            {!editable && (
              <div className='alert alert-warning mb-3'>
                Esta campanha está <strong>{STATUS_LABEL[c.status].toLowerCase()}</strong> e não pode ser editada. Pause-a para editar.
              </div>
            )}

            {/* Schedule */}
            <Card className='mb-3'>
              <CardBody>
                <h6 className='fw-semibold mb-3'>Horários de disparo</h6>
                <div className='d-flex flex-column gap-1'>
                  {schedule.map(d => (
                    <div key={d.day_of_week} className='d-flex align-items-center gap-3 small'>
                      <span className='fw-medium' style={{ width: 32 }}>{DAYS_LABEL[d.day_of_week]}</span>
                      {d.enabled ? (
                        <span className='text-muted'>{d.start_time?.slice(0, 5)} — {d.end_time?.slice(0, 5)}</span>
                      ) : (
                        <span className='text-muted'>Inativo</span>
                      )}
                    </div>
                  ))}
                </div>
                {schedule[0] && (
                  <div className='mt-3 pt-3 border-top d-flex gap-4 small text-muted'>
                    <span><strong>Intervalo:</strong> {schedule[0].interval_min}–{schedule[0].interval_max}s</span>
                    <span><strong>Lote:</strong> {schedule[0].batch_min}–{schedule[0].batch_max} msgs</span>
                    <span><strong>Pausa:</strong> {schedule[0].pause_min}–{schedule[0].pause_max}min</span>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Fluxo de Abordagem */}
            <Card className='mb-3'>
              <CardBody>
                <h6 className='fw-semibold mb-3'>Fluxo de Abordagem</h6>
                {flowSteps.length === 0 ? (
                  <p className='text-muted small'>Nenhum fluxo configurado.</p>
                ) : (
                  [...flowSteps].sort((a, b) => a.step_order - b.step_order).map(step => (
                    <div key={step.id} className='border rounded p-3 mb-3' style={{ background: '#fafafa' }}>
                      <div className='d-flex align-items-center gap-2 mb-2'>
                        <span className='fw-bold text-muted' style={{ fontSize: 13 }}>{step.step_order}</span>
                        <span className='badge' style={{
                          background: step.type === 'isca' ? '#fff3cd' : step.type === 'media' ? '#f0eeff' : '#e8f5e9',
                          color: step.type === 'isca' ? '#856404' : step.type === 'media' ? '#7366ff' : '#2e7d32',
                          fontSize: 11,
                        }}>
                          {step.type === 'isca' ? 'Isca' : step.type === 'media' ? 'Mídia' : 'Texto'}
                        </span>
                        <span className='fw-semibold' style={{ fontSize: 14 }}>{step.title}</span>
                      </div>
                      {step.type === 'isca' ? (
                        <p className='text-muted small mb-0'>
                          O engine envia uma frase isca aleatória configurada em Configurações → Mensagens Isca.
                        </p>
                      ) : (
                        <>
                          {step.instruction && (
                            <div className='mb-2'>
                              <span className='text-uppercase fw-semibold' style={{ fontSize: 10, color: '#888' }}>Instrução para o agente</span>
                              <p className='mb-0 mt-1' style={{ fontSize: 13 }}>{step.instruction}</p>
                            </div>
                          )}
                          {step.example && (
                            <div>
                              <span className='text-uppercase fw-semibold' style={{ fontSize: 10, color: '#888' }}>Exemplo de resposta</span>
                              <p className='mb-0 mt-1' style={{ fontSize: 13, fontStyle: 'italic', color: '#555' }}>{step.example}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))
                )}
              </CardBody>
            </Card>

            {editable && !isAdmin && (
              <div className='d-flex justify-content-end' style={{ marginBottom: '40px' }}>
                <Button color='primary' onClick={() => navigate(`/campanhas/${id}/editar`)}>
                  Editar configurações
                </Button>
              </div>
            )}
          </TabPane>

        </TabContent>
      </Container>
    </>
  );
}

function MetricCard({ label, value }) {
  return (
    <Card>
      <CardBody>
        <p className='text-muted text-uppercase mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>{label}</p>
        <p className='h4 fw-bold mb-0'>{typeof value === 'number' ? value.toLocaleString('pt-BR') : value}</p>
      </CardBody>
    </Card>
  );
}
